import React, { useEffect, useState } from "react";
import {
  TextField,
  Button,
  Grid2,
  Container,
  Typography,
  Paper,
  debounce,
  Autocomplete,
  MenuItem,
  Select,
  IconButton,
  SelectChangeEvent,
  Divider,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Course, courseInit } from "../../model/entity/Course";
import {
  CourseValidationObj,
  courseValidationObjInit,
  validateCourse,
} from "../../model/validation/CourseValidation";
import handleAxiosError from "../../components/ErrorHandler";
import { Coach } from "../../model/entity/Coach";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Loading } from "../../components/Loading";
import { DayOfWeek } from "../../model/common/DayOfWeek";
import {
  AddBoxOutlined,
  IndeterminateCheckBoxOutlined,
} from "@mui/icons-material";
import {
  generateTimeSlotOptions,
  notifErr,
  notifOk,
} from "../../components/Functions";
import { TrainingArea } from "../../model/entity/TrainingArea";
import { TimeSlotDTO, timeSlotDTOInit } from "../../model/entity/TimeSlotDTO";
import { TimeSlotAtTrainingAreaDTO } from "../../model/entity/TimeSlotAtTrainingAreaDTO";
import { CoachLeadsCourseDTO } from "../../model/entity/CoachLeadsCourseDTO";
import { TrainingSchedule } from "../../model/entity/TrainingSchedule";
import { getFetcher, postFetcher } from "../../components/Fetchers";

const NewCourse = () => {
  const navigate = useNavigate();
  const [trainingSchedule, setTrainingSchedule] =
    useState<TrainingSchedule | null>(null);
  const [triedSubmit, setTriedSubmit] = useState(false);
  const [trainingAreas, setTrainingAreas] = useState<TrainingArea[] | null>(
    null
  );
  const [coaches, setCoaches] = useState<Coach[] | null>(null);
  const [course, setCourse] = useState<Course>(courseInit);
  const [courseErr, setCourseErr] = useState<CourseValidationObj>(
    courseValidationObjInit
  );

  useEffect(() => {
    const fetchData = async (url: string, callbackFn: any) => {
      try {
        const response = await getFetcher(url);
        callbackFn(response.data);
      } catch (err) {
        notifErr(handleAxiosError(err));
      }
    };

    fetchData(
      `${process.env.REACT_APP_API_URL}/schedules/active`,
      setTrainingSchedule
    );
    fetchData(`${process.env.REACT_APP_API_URL}/coaches`, (data: Coach[]) => {
      const sortedData = data.sort((a: Coach, b: Coach) => {
        const res = a.firstName.localeCompare(b.firstName);
        return res ? res : a.lastName.localeCompare(b.lastName);
      });
      setCoaches(sortedData);
    });
    fetchData(
      `${process.env.REACT_APP_API_URL}/training-areas/sorted-by-code`,
      setTrainingAreas
    );
  }, []);

  useEffect(() => {
    if (triedSubmit) setCourseErr(validateCourse(course));
  }, [course, triedSubmit]);

  const handleCourseInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.name === "color") {
      debouncedColorChange(e.target.value);
    } else {
      setCourse({ ...course, [e.target.name]: e.target.value });
    }
  };

  const handleAddTimeSlotRow = () => {
    const updatedTimeSlots = [...course.timeSlots, timeSlotDTOInit];
    setCourse({ ...course, timeSlots: updatedTimeSlots });
  };

  const handleRemoveTimeSlotRow = (index: number) => {
    const updatedTimeSlots = course.timeSlots.filter((ts, i) => i !== index);
    setCourse({ ...course, timeSlots: updatedTimeSlots });
  };

  const handleTimeSlotInputChange = (e: SelectChangeEvent, index: number) => {
    const updatedTimeSlots = course.timeSlots.map((ts, i) =>
      i === index ? { ...ts, [e.target.name]: e.target.value } : ts
    );
    setCourse({ ...course, timeSlots: updatedTimeSlots });
  };

  const handleCoachChange = (newValues: Coach[]) => {
    const oldValues = course.coachLeadsCourses.map((clc) => clc.coach);

    const addedItem = newValues.find(
      (val) => !oldValues.map((oldVal) => oldVal.id).includes(val.id)
    );
    const removedItem = oldValues.find(
      (val) => !newValues.map((newVal) => newVal.id).includes(val.id)
    );

    if (addedItem) {
      const updatedArr: CoachLeadsCourseDTO[] = [
        ...course.coachLeadsCourses,
        {
          id: null,
          coach: addedItem,
        },
      ];
      setCourse({ ...course, coachLeadsCourses: updatedArr });
    }

    if (removedItem) {
      const updatedArr: CoachLeadsCourseDTO[] = course.coachLeadsCourses.filter(
        (clc) => clc.coach.id !== removedItem.id
      );
      setCourse({ ...course, coachLeadsCourses: updatedArr });
    }
  };

  const handleTrainingAreaChange = (e: any, tsIndex: number) => {
    const { value } = e.target;
    const newValues = value as number[];

    const currentTimeSlot = course.timeSlots.at(tsIndex);
    if (!currentTimeSlot) {
      notifErr("Nastala neočekávaná chyba.");
      return;
    }
    const oldValues = currentTimeSlot.timeSlotAtTrainingAreas.map(
      (ta) => ta.trainingArea.id!
    );

    const addedItem = newValues.find((val) => !oldValues.includes(val));
    const removedItem = oldValues.find((val) => !newValues.includes(val));

    if (addedItem) {
      const trainingArea = trainingAreas?.find((ta) => ta.id === addedItem);
      if (!trainingArea) {
        notifErr("Nastala neočekávaná chyba.");
        return;
      }
      const updatedArr: TimeSlotAtTrainingAreaDTO[] = [
        ...currentTimeSlot.timeSlotAtTrainingAreas,
        {
          id: null,
          trainingArea: trainingArea,
        },
      ];
      const updatedTimeSlot: TimeSlotDTO = {
        ...currentTimeSlot,
        timeSlotAtTrainingAreas: updatedArr,
      };
      const updatedTimeSlots: TimeSlotDTO[] = course.timeSlots.map(
        (ts, index) => (index !== tsIndex ? ts : updatedTimeSlot)
      );
      setCourse({ ...course, timeSlots: updatedTimeSlots });
    }

    if (removedItem) {
      const updatedArr: TimeSlotAtTrainingAreaDTO[] =
        currentTimeSlot.timeSlotAtTrainingAreas.filter(
          (ta) => ta.trainingArea.id !== removedItem
        );
      const updatedTimeSlot: TimeSlotDTO = {
        ...currentTimeSlot,
        timeSlotAtTrainingAreas: updatedArr,
      };
      const updatedTimeSlots: TimeSlotDTO[] = course.timeSlots.map(
        (ts, index) => (index !== tsIndex ? ts : updatedTimeSlot)
      );
      setCourse({ ...course, timeSlots: updatedTimeSlots });
    }
  };

  const debouncedColorChange = debounce((value: string) => {
    setCourse((prev) => ({ ...prev, color: value }));
  }, 5);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTriedSubmit(true);

    const courseErrors = validateCourse(course);
    setCourseErr(courseErrors);

    if (Object.values(courseErrors).some((val) => val === false)) {
      if (!courseErrors.timeSlots) {
        notifErr("Zkontrolujte údaje v časových slotech.");
      }
      return;
    }

    if (trainingSchedule === null) {
      notifErr("Nastala chyba při načítání dat.");
      return;
    }

    const updatedCourse = { ...course, trainingSchedule: trainingSchedule };

    try {
      await postFetcher(
        `${process.env.REACT_APP_API_URL}/courses`,
        updatedCourse
      );
      notifOk("Kurz byl úspěšně vytvořen.");
      navigate("/overview/courses");
    } catch (err) {
      notifErr(handleAxiosError(err));
    }
  };

  const handleCancel = () => {
    navigate("/overview/courses");
  };

  const timeOptions = generateTimeSlotOptions(0, 24, true);

  if (coaches === null || trainingAreas === null || trainingSchedule === null)
    return <Loading />;

  return (
    <Container
      maxWidth="md"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        pb: "2rem",
      }}
    >
      <Paper elevation={3} sx={{ padding: "1.5rem", width: "100%" }}>
        <Typography variant="h5" sx={{ marginBottom: "1.5rem" }}>
          Nový kurz
        </Typography>
        <Grid2 container spacing={3} size={{ xs: 12, md: 6 }}>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              size="small"
              label="Název kurzu"
              required
              name="name"
              value={course.name}
              error={!courseErr.name}
              onChange={handleCourseInputChange}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <Autocomplete
              size="small"
              multiple
              disableCloseOnSelect={true}
              limitTags={1}
              options={coaches}
              getOptionKey={(option) => option.id!}
              getOptionLabel={(option) =>
                option.firstName + " " + option.lastName
              }
              filterSelectedOptions={true}
              filterOptions={(o, { inputValue }) =>
                o.filter((option) =>
                  (option.firstName + " " + option.lastName)
                    .toLowerCase()
                    .includes(inputValue.toLowerCase())
                )
              }
              renderInput={(params) => (
                <TextField {...params} label="Trenéři" placeholder="Najít..." />
              )}
              onChange={(e, val) => handleCoachChange(val)}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              size="small"
              label="Kapacita"
              type="number"
              required
              name="capacity"
              value={course.capacity}
              error={!courseErr.capacity}
              onChange={handleCourseInputChange}
              slotProps={{
                input: {
                  onFocus: (event) => {
                    event.target.select();
                  },
                },
              }}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              size="small"
              label="Členské příspěvky (Kč)"
              type="number"
              required
              name="fee"
              value={course.fee}
              error={!courseErr.fee}
              onChange={handleCourseInputChange}
              slotProps={{
                input: {
                  onFocus: (event) => {
                    event.target.select();
                  },
                },
              }}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              size="small"
              label="Popisek"
              multiline
              rows={4}
              name="description"
              value={course.description}
              error={!courseErr.description}
              onChange={handleCourseInputChange}
            />
          </Grid2>
          <Grid2
            size={{ xs: 12, md: 4 }}
            sx={{ display: "flex", flexDirection: "column", justifyContent: "space-between", alignItems: "left" }}
          >
            <Grid2
              sx={{
                display: "flex",
                justifyContent: "space-between",
                gap: "1rem",
              }}
            >
              <TextField
                fullWidth
                size="small"
                label="Barva"
                type="color"
                required
                name="color"
                value={course.color}
                error={!courseErr.color}
                onChange={handleCourseInputChange}
              />
              <TextField
                fullWidth
                size="small"
                label="Barva hex"
                required
                name="color"
                value={course.color}
                error={!courseErr.color}
                onChange={handleCourseInputChange}
              />
            </Grid2>
            <FormControlLabel
              control={<Checkbox />}
              checked={course.competitive}
              label="Závodní skupina"
              onChange={() =>
                setCourse({ ...course, competitive: !course.competitive })
              }
              sx={{ marginBottom: ".75rem" }}
            />
          </Grid2>
        </Grid2>

        {/* SCHEDULE */}

        <Grid2 container sx={{ my: 4 }}>
          <Grid2 size={{ xs: 12 }} container>
            <Grid2 size={{ xs: 0.7 }}>
              <IconButton
                sx={{ padding: 0, color: "#0f0f0f" }}
                onClick={handleAddTimeSlotRow}
              >
                <AddBoxOutlined />
              </IconButton>
            </Grid2>

            <Grid2 size={{ xs: 3 }}>
              <Typography variant="body1" fontWeight="bold">
                Den
              </Typography>
            </Grid2>
            <Grid2 size={{ xs: 1.7 }} sx={{ pl: 0.3 }}>
              <Typography variant="body1" fontWeight="bold">
                Od
              </Typography>
            </Grid2>
            <Grid2 size={{ xs: 1.7 }} sx={{ pl: 0.5 }}>
              <Typography variant="body1" fontWeight="bold">
                Do
              </Typography>
            </Grid2>
            <Grid2 size={{ xs: 3 }} sx={{ pl: 0.8 }}>
              <Typography variant="body1" fontWeight="bold">
                Sportoviště
              </Typography>
            </Grid2>
          </Grid2>

          <Divider
            sx={{
              width: "84%",
              my: 2,
              borderBottomWidth: "1px",
            }}
          />

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            {course.timeSlots.map((timeslot, index) => (
              <Grid2
                size={{ xs: 12 }}
                container
                spacing={2}
                key={index}
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <Grid2 size={{ xs: 0.7 }}>
                  <IconButton
                    sx={{ padding: 0, color: "#0f0f0f" }}
                    onClick={() => handleRemoveTimeSlotRow(index)}
                  >
                    <IndeterminateCheckBoxOutlined />
                  </IconButton>
                </Grid2>

                <Grid2 size={{ xs: 3 }}>
                  <Select
                    size="small"
                    fullWidth
                    name="day"
                    value={timeslot.day}
                    onChange={(e) => handleTimeSlotInputChange(e, index)}
                    sx={{
                      maxHeight: "2.2rem",
                    }}
                  >
                    {Object.entries(DayOfWeek).map(([key, value]) => (
                      <MenuItem key={key} value={key}>
                        {value}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid2>

                <Grid2 size={{ xs: 1.7 }}>
                  <Select
                    fullWidth
                    name="startTime"
                    value={timeslot.startTime}
                    onChange={(e) => handleTimeSlotInputChange(e, index)}
                    sx={{
                      maxHeight: "2.2rem",
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          maxHeight: "15rem",
                          overflowY: "auto",
                        },
                      },
                    }}
                  >
                    {timeOptions.map((time, index) => (
                      <MenuItem key={index} value={time}>
                        {time}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid2>

                <Grid2 size={{ xs: 1.7 }}>
                  <Select
                    fullWidth
                    name="endTime"
                    value={timeslot.endTime}
                    onChange={(e) => handleTimeSlotInputChange(e, index)}
                    sx={{
                      maxHeight: "2.2rem",
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          maxHeight: "15rem",
                          overflowY: "auto",
                        },
                      },
                    }}
                  >
                    {timeOptions.map((time, index) => (
                      <MenuItem key={index} value={time}>
                        {time}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid2>

                <Grid2 size={{ xs: 3 }}>
                  <Select
                    multiple
                    size="small"
                    fullWidth
                    value={
                      course.timeSlots
                        .at(index)
                        ?.timeSlotAtTrainingAreas.map(
                          (ta) => ta.trainingArea.id
                        ) ?? []
                    }
                    onChange={(e) => handleTrainingAreaChange(e, index)}
                    sx={{
                      maxHeight: "2.2rem",
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          maxHeight: "15rem",
                          overflowY: "auto",
                        },
                      },
                    }}
                  >
                    {trainingAreas.map((area) => (
                      <MenuItem key={area.id!} value={area.id!}>
                        {area.code}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid2>
              </Grid2>
            ))}
          </LocalizationProvider>
        </Grid2>

        {/* END SCHEDULE */}

        <Grid2 container size={{ xs: 12 }} justifyContent="right">
          <Grid2>
            <Button
              variant="outlined"
              className="cancelBtn"
              onClick={handleCancel}
              sx={{ mr: 1.5 }}
            >
              Zrušit
            </Button>
          </Grid2>
          <Grid2>
            <Button
              variant="outlined"
              className="submitBtn"
              onClick={handleSubmit}
            >
              Přidat
            </Button>
          </Grid2>
        </Grid2>
      </Paper>
    </Container>
  );
};

export default NewCourse;
